import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../utils/api';
import { saveMfaGrace, clearMfaGrace } from '../utils/mfaGrace';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(() => JSON.parse(localStorage.getItem('user') || 'null'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      api.get('/auth/me')
        .then(r => { setUser(r.data); localStorage.setItem('user', JSON.stringify(r.data)); })
        .catch(() => { localStorage.removeItem('token'); localStorage.removeItem('user'); setUser(null); })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    // MFA required — return the pending state to the caller (Login.jsx handles it)
    if (data.mfaRequired) return { mfaRequired: true, mfaToken: data.mfaToken };
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(data.user));
    // Days left to enrol in 2FA when the organisation requires it (MfaGraceNotice shows it);
    // absent means nothing is owed, which also clears a previous person's reminder.
    saveMfaGrace(data.mfaGrace);
    setUser(data.user);
    return data.user;
  };

  const setAuthFromToken = (token, userObj) => {
    clearMfaGrace();   // signed in with a second factor: nothing left to set up
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userObj));
    setUser(userObj);
  };

  const logout = async () => {
    // Blacklist the JWT on the server so it can't be reused (HIGH-C02)
    try { await api.post('/compliance/logout'); } catch {}
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('prev_token');
    clearMfaGrace();
    setUser(null);
  };

  const startImpersonation = (token, org) => {
    localStorage.setItem('prev_token', localStorage.getItem('token') || '');
    localStorage.setItem('token', token);
    const impUser = {
      // role 'admin' (not 'org_admin') so the impersonated session passes the app's
      // role guards (A=['admin','office_user']); the impersonating flag drives the banner.
      id: org.id, email: org.email, role: 'admin',
      name: org.name, firstName: org.name,
      impersonating: true,
    };
    localStorage.setItem('user', JSON.stringify(impUser));
    setUser(impUser);
  };

  const exitImpersonation = async () => {
    const prev = localStorage.getItem('prev_token');
    localStorage.removeItem('prev_token');
    if (prev) {
      localStorage.setItem('token', prev);
      try {
        const { data } = await api.get('/auth/me');
        localStorage.setItem('user', JSON.stringify(data));
        setUser(data);
      } catch {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
      }
    } else {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
    }
  };

  return (
    <AuthContext.Provider value={{
      user, login, logout, loading, setAuthFromToken,
      isAdmin: ['admin', 'super_admin'].includes(user?.role),
      isOffice: ['admin', 'office_user', 'super_admin'].includes(user?.role),
      startImpersonation,
      exitImpersonation,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
